import React from 'react';
import { Table, TableBody, TableCell, TableHead, TableRow, TableSortLabel } from '@mui/material';
import { TableContainer, Paper } from '@mui/material';

interface SimpleTableCell {
  span?: number | null;
  cell: React.ReactNode | null;
}

interface SimpleTableProps {
  headers?: React.ReactNode[];
  rows: (React.ReactNode | SimpleTableCell | null)[][];
  hidden?: (boolean | null)[] | null;
  filter?: (boolean | null)[] | null;
  name?: string | null;
  rowIds?: any[] | null;
  fullWidth?: boolean;
  compact?: boolean;
  onRowClick?: (index: number) => void;
  onHeaderClick?: (headerIndex: number) => void;
  sortColumn?: string | null;
  sortDirection?: 'asc' | 'desc';
  sortableHeaders?: string[];
  getRowStyle?: (index: number) => React.CSSProperties;
  tableProps?: {
    [key: string]: any;
  };
}

/**
 * Simple table component without pagination.
 * Supports sorting, row filtering, cell spanning, and custom row styles.
 * Compact mode removes borders and reduces padding.
 */
export default function SimpleTable(props: SimpleTableProps) {
  return (
    <TableContainer
      component={Paper}
      sx={{
        width: props.fullWidth === undefined || props.fullWidth ? '100%' : 'fit-content',
        ...(props.compact && {
          boxShadow: 'none',
          '& .MuiTable-root': {
            borderCollapse: 'separate',
            borderSpacing: 0,
          },
        }),
      }}
    >
      <Table {...props.tableProps}>
        {props.headers && props.headers.length > 0 && (
          <TableHead>
            <TableRow>
              {props.headers
                .filter((h, index) => (props.hidden ? !props.hidden[index] : true))
                .map((h, i) => {
                  const isSortable = props.sortableHeaders?.includes(h as string) || false;
                  const isActiveSort = props.sortColumn === h;

                  return (
                    <TableCell
                      key={`head_cell_${i}_${props.name}`}
                      sx={{
                        ...(props.compact && {
                          border: 'none',
                          padding: '4px 8px',
                          fontSize: '0.875rem',
                          fontWeight: 'bold',
                        }),
                      }}
                    >
                      {isSortable ? (
                        <TableSortLabel
                          active={isActiveSort}
                          direction={isActiveSort ? props.sortDirection : 'asc'}
                          onClick={() => props.onHeaderClick?.(i)}
                          sx={{
                            '&:hover': {
                              color: 'primary.main',
                            },
                            '& .MuiTableSortLabel-icon': {
                              opacity: 0.5,
                            },
                            '&.Mui-active .MuiTableSortLabel-icon': {
                              opacity: 1,
                            },
                          }}
                        >
                          {h}
                        </TableSortLabel>
                      ) : (
                        h
                      )}
                    </TableCell>
                  );
                })}
            </TableRow>
          </TableHead>
        )}
        <TableBody>
          {props.rows
            .filter((row, index) => (props.filter ? props.filter[index] : true))
            .map((row, index) => (
              <TableRow
                key={`table_row_${(props.rowIds && props.rowIds[index]) || `${index}`}_${props.name}`}
                onClick={() => props.onRowClick?.(index)}
                sx={{
                  '&:last-child td, &:last-child th': { border: 0 },
                  '&:nth-child(odd)': { backgroundColor: props.compact ? 'transparent' : '#f9f9f9' },
                  cursor: props.onRowClick ? 'pointer' : 'default',
                  '&:hover': props.onRowClick
                    ? {
                        backgroundColor: '#f0f0f0',
                        transition: 'background-color 0.2s ease',
                      }
                    : {},
                  ...(props.getRowStyle ? props.getRowStyle(index) : {}),
                }}
              >
                {row
                  .filter((r, index) => (props.hidden ? !props.hidden[index] : true))
                  .map((cell, index) => (
                    <TableCell
                      key={`table_row_cell_${index}_${props.name}`}
                      {...(cell && (cell as any)['span'] ? { colSpan: (cell as any).span } : {})}
                      sx={{
                        verticalAlign: 'top',
                        ...(props.compact && {
                          border: 'none',
                          padding: '4px 8px',
                          fontSize: '0.875rem',
                        }),
                      }}
                    >
                      {(cell && (cell as any).cell) || cell}
                    </TableCell>
                  ))}
              </TableRow>
            ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
